import { Directive, inject, TemplateRef } from '@angular/core';
import { HisSelectFilePropertyTemplateContext } from '../model/his-select-file-property-template-context.model';

/**
 * Marks an {@code <ng-template>} as the "icon" custom-property display of a
 * {@link HisSelectFileComponent}. Applying this directive gives the template's
 * {@code let-} variables a proper type (via {@link ngTemplateContextGuard}), so
 * developers get IDE auto-completion and compile-time checking:
 *
 * ```html
 * <his-select-file ...>
 *     <ng-template hisPropertyCustomDisplayIcon let-file let-properties="properties">
 *         <!-- `file` is HisSelectFile, `properties` is typed -->
 *     </ng-template>
 * </his-select-file>
 * ```
 */
@Directive( {
    standalone: false,
    selector: 'ng-template[hisPropertyCustomDisplayIcon]',
} )
export class HisSelectFilePropertyDisplayIconDirective {

    /** The typed template reference this directive is attached to. */
    readonly templateRef = inject<TemplateRef<HisSelectFilePropertyTemplateContext>>( TemplateRef );

    /** Narrows the template context type for the Angular template type-checker. */
    static ngTemplateContextGuard(
        _directive: HisSelectFilePropertyDisplayIconDirective,
        _context: unknown
    ): _context is HisSelectFilePropertyTemplateContext {
        return true;
    }
}

/**
 * Marks an {@code <ng-template>} as the custom-property display of a
 * {@link HisSelectFileComponent} (rendered inside the resource-properties
 * overlay panel). Applying this directive gives the template's {@code let-}
 * variables a proper type (via {@link ngTemplateContextGuard}), so developers
 * get IDE auto-completion and compile-time checking:
 *
 * ```html
 * <his-select-file ...>
 *     <ng-template hisPropertyCustomDisplay let-file let-properties="properties">
 *         <!-- `file` is HisSelectFile, `properties` is typed -->
 *     </ng-template>
 * </his-select-file>
 * ```
 */
@Directive( {
    standalone: false,
    selector: 'ng-template[hisPropertyCustomDisplay]',
} )
export class HisSelectFilePropertyDisplayDirective {

    /** The typed template reference this directive is attached to. */
    readonly templateRef = inject<TemplateRef<HisSelectFilePropertyTemplateContext>>( TemplateRef );

    /** Narrows the template context type for the Angular template type-checker. */
    static ngTemplateContextGuard(
        _directive: HisSelectFilePropertyDisplayDirective,
        _context: unknown
    ): _context is HisSelectFilePropertyTemplateContext {
        return true;
    }
}
